import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, TextField, Button, Chip, Stack, Link, IconButton, Tooltip,
} from '@mui/material';
import {
  EventNoteOutlined as DemoIcon, MailOutline as MailIcon, Refresh as RefreshIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { apiErrorMessage } from '../passwordPolicy';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const BASE = `${API_URL}/api/v1/superadmin/demo-requests`;

const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, {
  day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '—');
const day = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
}) : null);

export default function DemoRequests() {
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const [req, rec] = await Promise.all([axios.get(BASE), axios.get(`${BASE}/recipients`)]);
      setRequests(req.data.requests || []);
      setTotal(req.data.total || 0);
      setRecipients(rec.data.recipients || []);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to load demo requests'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    // Several at once: "a@x.com, b@y.com" or one per line.
    const emails = email.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!emails.length) return;
    setAdding(true);
    const failed = [];
    for (const addr of emails) {
      try {
        await axios.post(`${BASE}/recipients`, { email: addr });
      } catch (err) {
        failed.push(addr);
        toast.error(apiErrorMessage(err, `Could not add ${addr}`));
      }
    }
    const added = emails.length - failed.length;
    if (added) toast.success(added === 1 ? 'Email added' : `${added} emails added`);
    setEmail(failed.join(', '));
    setAdding(false);
    load();
  };

  const handleRemove = async (r) => {
    try {
      await axios.delete(`${BASE}/recipients/${r.id}`);
      toast.success(`${r.email} removed`);
      setRecipients((list) => list.filter((x) => x.id !== r.id));
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not remove that email'));
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete the request from ${r.full_name} (${r.email})? This can't be undone.`)) return;
    try {
      await axios.delete(`${BASE}/${r.id}`);
      toast.success('Request deleted');
      setRequests((list) => list.filter((x) => x.id !== r.id));
      setTotal((n) => Math.max(0, n - 1));
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not delete that request'));
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress sx={{ color: '#c9a227' }} /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <DemoIcon sx={{ color: '#0d9488' }} />
        <Typography variant="h4" fontWeight={800}>Demo requests</Typography>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Everyone who used &ldquo;Book a call&rdquo; on scribelive.us, newest first.
      </Typography>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(15, 23, 42, 0.12)', mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700}>Who gets emailed</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every address here receives an email as soon as someone books a call.
        </Typography>
        <Box component="form" onSubmit={handleAdd} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            size="small"
            label="Email address"
            placeholder="name@scribelive.us — separate several with commas"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ flex: '1 1 320px' }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={adding || !email.trim()}
            sx={{ bgcolor: '#c9a227', '&:hover': { bgcolor: '#a16207' } }}
          >
            {adding ? 'Adding…' : 'Add'}
          </Button>
        </Box>
        {recipients.length === 0 ? (
          <Alert severity="warning">No one is on the list yet, so new requests only appear on this page.</Alert>
        ) : (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {recipients.map((r) => (
              <Chip
                key={r.id}
                icon={<MailIcon />}
                label={r.email}
                onDelete={() => handleRemove(r)}
                title={r.added_by ? `Added by ${r.added_by}` : undefined}
              />
            ))}
          </Stack>
        )}
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {total} {total === 1 ? 'request' : 'requests'}
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Box>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Received</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Clinic</TableCell>
              <TableCell>Preferred slot</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.length === 0 ? (
              <TableRow><TableCell colSpan={6}>No one has booked a call yet.</TableCell></TableRow>
            ) : requests.map((r) => (
              <TableRow key={r.id} hover sx={{ verticalAlign: 'top' }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2">{when(r.created_at)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight={600} variant="body2">{r.full_name}</Typography>
                  <Link href={`mailto:${r.email}`} variant="body2" underline="hover">{r.email}</Link>
                  {r.phone && <Typography variant="body2" color="text.secondary">{r.phone}</Typography>}
                  {r.role && <Typography variant="caption" color="text.secondary">{r.role}</Typography>}
                </TableCell>
                <TableCell>
                  <Typography fontWeight={600} variant="body2">{r.clinic_name}</Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {[r.providers ? `${r.providers} providers` : null, r.ehr].filter(Boolean).join(' • ') || '—'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2">{day(r.preferred_date) || '—'}</Typography>
                  {r.preferred_slot && <Typography variant="caption" color="text.secondary">{r.preferred_slot}</Typography>}
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{r.notes || '—'}</Typography>
                </TableCell>
                <TableCell padding="checkbox">
                  <Tooltip title="Delete (spam or duplicate)">
                    <IconButton size="small" onClick={() => handleDelete(r)} aria-label={`Delete request from ${r.full_name}`}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
